/* eslint-disable react/prop-types */
import ClockIcon from "@Icons/ClockIcon";
import styles from "./citySuggestionsMobile.module.scss";
import { FC, useCallback, useRef, useState } from "react";
import LocationIcon from "@Icons/LocationIcon";
import UniversityIcon from "@Icons/UniversityIcon";
import { useHistory } from "hooks/useHistory";
import useIsDesktop from "hooks/useIsDesktop";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import customHistoryPush from "@Utils/customHistoryPush";
import wrapperHOC from "@Utils/wrapperHOC";
import config from "@Config/index";
import useTranslationHook from "hooks/useTranslationHook";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import useWhitelabel from "hooks/useWhitelabel";
import { logAppEvent, logSingularEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, CLICKED_ITEM } from "@Utils/webApputils/analyticEvents";

const CitySuggestionsMobile: FC<any> = ({
  cities,
  title,
  handleGtagEvents,
  handleGtagScrollEvents,
  gtagEventAction,
  gtagScrollAction,
  setShowSearchModal,
  dataTestId,
  fromSearchPage = false,
  singleRowScrollable = false,
  twoRowScrollable = false,
}) => {
  const history = useHistory();
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();
  const { partner_uuid, isDemoSubdomain } = useWhitelabel();
  const eventNameForApp = () => {
    switch (gtagEventAction) {
      case "clicked_recently_searched_result":
        return CLICKED_ITEM.RECENTLY_SEARCHED;
      case "clicked_popular_locations_result":
        return CLICKED_ITEM.POPULAR_LOCATIONS;
      case "clicked_top_universities_result":
        return CLICKED_ITEM.TOP_UNIVERSITIES;
      default:
        return undefined;
    }
  };
  const handleCityClick = (canonicalName = null, regionType = null, name = null) => {
    if (handleGtagEvents) {
      logAppEvent({
        eventName: APP_EVENTS.CTA_CLICKED,
        payload: {
          cta_name: eventNameForApp,
          canonical_name: canonicalName,
          entity_name: name,
          entity_type: regionType,
          entity_grid: title.toLowerCase().replace("⚡", ""),
        },
      });

      handleGtagEvents({
        action: gtagEventAction,
        label: canonicalName,
        canonical_name: canonicalName,
        entity_name: name,
        entity_type: regionType,
        entity_grid: title.toLowerCase().replace("⚡", ""),
      });
    }
    customHistoryPush(
      history,
      convertToWhitelabelURL(`/places/search/${canonicalName}`, isDemoSubdomain, partner_uuid),
      fromSearchPage,
    );
    setShowSearchModal(false);
    logSingularEvent("sngSearch", { sngAttrSearchString: name });
  };
  const handleScroll = () => {
    if (handleGtagScrollEvents) {
      handleGtagScrollEvents({
        action: gtagScrollAction,
        entity_grid: title.toLowerCase().replace("⚡", ""),
      });
    }
  };
  let titleIcon = <LocationIcon />;
  if (title.includes("Recently Searched")) {
    titleIcon = <ClockIcon height={18} width={18} />;
  } else if (title.includes("Top Universities")) {
    titleIcon = <UniversityIcon />;
  }

  const uniFlag = title.includes("Top Universities") || title.includes("Top University");

  const validCities = cities?.filter((city: any) => city.name && city.canonical_name) || [];

  // Match Desktop threshold: >5 for universities, >10 for cities
  const shouldUseTwoRow = twoRowScrollable && validCities.length > (uniFlag || !isDesktop ? 5 : 10);

  // Track whether the scrollable container has reached the end
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

  const handleContainerScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
    setIsScrolledToEnd(atEnd);
  }, []);

  const getContainerClass = () => {
    if (shouldUseTwoRow) {
      return formatClassNames(
        styles.panelSectionListContainerTwoRow,
        isScrolledToEnd && styles.noFade,
      );
    }
    if (singleRowScrollable || twoRowScrollable) {
      return formatClassNames(
        styles.panelSectionListContainerScrollable,
        isScrolledToEnd && styles.noFade,
      );
    }
    return styles.panelSectionListContainer;
  };

  const getListClass = () => {
    if (shouldUseTwoRow) return styles.panelSectionListTwoRow;
    if (singleRowScrollable || twoRowScrollable) return styles.panelSectionListSingleRow;
    return styles.panelSectionList;
  };

  const renderCityItem = (city: any, i: number, keyPrefix: string) => (
    <div
      role="none"
      onClick={() => handleCityClick(city.canonical_name, city.region_type, city.name)}
      key={`${city.id + city.canonical_name}${keyPrefix}${i}`}
      className={formatClassNames(styles.panelText, isDesktop && styles.desktop)}
      data-testid={city.name || dataTestId}
    >
      {title == "Recently Searched" && !isDesktop ? <ClockIcon /> : ""}
      <div
        data-testid={`search-suggestion-${title}-cities`}
        className={uniFlag || config.IS_CHINA_APP ? "" : styles.panelTextInner}
      >
        {config.IS_CHINA_APP && city.name_zh ? `${city.name_zh} (${city.name})` : city.name}
      </div>
    </div>
  );

  const renderTwoRowLayout = () => {
    const row1: any[] = [];
    const row2: any[] = [];
    validCities.forEach((city: any, i: number) => {
      if (i % 2 === 0) {
        row1.push(renderCityItem(city, i, "row1"));
      } else {
        row2.push(renderCityItem(city, i, "row2"));
      }
    });
    return (
      <>
        <div className={styles.panelSectionListTwoRowInner}>{row1}</div>
        <div className={styles.panelSectionListTwoRowInner}>{row2}</div>
      </>
    );
  };

  // Old mweb layout: split cities into two static rows (top half / bottom half)
  const shouldUseOldMwebLayout = !isDesktop && !singleRowScrollable && !twoRowScrollable;

  const renderOldMwebLayout = () => {
    const middleIndex = validCities.length / 2 >= 4 ? Math.ceil(validCities.length / 2) : 4;
    const topArr = validCities.slice(0, middleIndex);
    const bottomArr = validCities.length > 4 ? validCities.slice(topArr.length) : [];

    return (
      <>
        <div className={styles.panelSectionList} onTouchEnd={() => handleScroll()}>
          {topArr.map((city: any, i: number) => renderCityItem(city, i, "topArr"))}
        </div>
        {bottomArr.length > 0 && (
          <div className={styles.panelSectionList}>
            {bottomArr.map((city: any, i: number) => renderCityItem(city, i, "bottomArr"))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={isDesktop && styles.desktopPanel}>
      <div
        className={formatClassNames(styles.panelSectionTitle, isDesktop && styles.desktop)}
        data-testid={`search-suggestion-${title}-heading`}
      >
        {titleIcon} {t(title)}
      </div>
      <div className={getContainerClass()} ref={containerRef} onScroll={handleContainerScroll}>
        {shouldUseOldMwebLayout && renderOldMwebLayout()}
        {!shouldUseOldMwebLayout && shouldUseTwoRow && (
          <div className={getListClass()} onTouchEnd={() => handleScroll()}>
            {renderTwoRowLayout()}
          </div>
        )}
        {!shouldUseOldMwebLayout && !shouldUseTwoRow && (
          <div className={getListClass()} onTouchEnd={() => handleScroll()}>
            {validCities.map((city: any, i: number) => renderCityItem(city, i, "item"))}
          </div>
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(CitySuggestionsMobile, {
  componentName: "CitySuggestionsMobile-SearchModalMobile-Global",
  showForChina: true,
});
